import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, TrendingUp, Users, GraduationCap, DollarSign, ClipboardList, MessageSquare, RefreshCcw } from "lucide-react";
import { UniHeader } from "@/pages/university/_lmsShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WINDOWS = [
  { key: "30d", label: "Last 30 Days" }, { key: "90d", label: "Last 90 Days" },
  { key: "ytd", label: "Year-to-Date" }, { key: "all", label: "All Time" },
];

export default function AdminExecutive() {
  const [window, setWindow] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem("kcis_admin_token");
      const r = await axios.get(`${API}/launch/admin/executive`, {
        params: { window }, headers: { Authorization: `Bearer ${t}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load dashboard");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [window]);

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Executive Dashboard" back="/admin/dashboard" />
      <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-executive-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" />Ecosystem-Wide Oversight
            </div>
            <h1 className="font-display text-3xl text-slate-900">KCIS Executive</h1>
          </div>
          <div className="flex items-center gap-2">
            {WINDOWS.map((w) => (
              <button key={w.key} onClick={() => setWindow(w.key)}
                className={`text-xs px-3 py-2 rounded-full ${window === w.key ? "bg-purple-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-purple-300"}`}
                data-testid={`exec-window-${w.key}`}>{w.label}</button>
            ))}
            <button onClick={load} className="text-xs text-purple-900 hover:underline inline-flex items-center gap-1"><RefreshCcw className="w-3 h-3" />Refresh</button>
          </div>
        </div>

        {loading || !data ? (
          <div className="text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : (
          <>
            {/* Revenue band */}
            <section className="rounded-2xl border border-purple-200 bg-purple-50/40 p-5 mb-6" data-testid="exec-revenue-band">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Tile big icon={DollarSign} label="Revenue (window)" value={`$${(data.revenue.total_usd).toLocaleString()}`} />
                <Tile icon={DollarSign} label="MRR" value={`$${data.revenue.mrr_usd.toLocaleString()}`} />
                <Tile icon={DollarSign} label="ARR" value={`$${data.revenue.arr_usd.toLocaleString()}`} />
                <Tile icon={Users} label="Active Subs" value={data.metrics.subscriptions_active} />
                <Tile icon={TrendingUp} label="Churn" value={`${data.revenue.churn_rate_percent}%`} />
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2 text-xs text-slate-600">
                <Sub label="University" value={`$${data.revenue.by_door.university.toLocaleString()}`} />
                <Sub label="Alumni" value={`$${data.revenue.by_door.alumni.toLocaleString()}`} />
                <Sub label="Practitioner" value={`$${data.revenue.by_door.practitioner.toLocaleString()}`} />
                <Sub label="Pending Subs" value={data.metrics.subscriptions_pending ?? 0} />
              </div>
            </section>

            {/* Pipeline */}
            <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6" data-testid="exec-pipeline">
              <Tile icon={Users} label="Leads" value={data.metrics.leads} />
              <Tile icon={ClipboardList} label="Applications" value={data.metrics.applications} />
              <Tile icon={GraduationCap} label="Active Students" value={data.metrics.students_active} />
              <Tile icon={GraduationCap} label="New Students" value={data.metrics.students_new_in_window} />
              <Tile icon={GraduationCap} label="Graduates" value={data.metrics.graduates} />
              <Tile icon={Users} label="Alumni Total" value={data.metrics.alumni_total} />
            </section>

            {/* Operations */}
            <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <Tile icon={Users} label="Practitioners" value={data.metrics.practitioners} />
              <Tile icon={ClipboardList} label="Clinical Assessments" value={data.metrics.assessments_clinic} />
              <Tile icon={ClipboardList} label="Identity Assessments" value={data.metrics.assessments_identity} />
              <Tile icon={ClipboardList} label="Protocols Generated" value={data.metrics.protocols_generated} />
              <Tile icon={MessageSquare} label="Community Posts" value={data.metrics.community_posts} />
              <Tile icon={MessageSquare} label="Community Replies" value={data.metrics.community_replies} />
            </section>

            {/* Risk */}
            <section className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5" data-testid="exec-risk">
              <div className="text-xs uppercase tracking-wide text-amber-800 mb-2">Outstanding Tuition Balances</div>
              <div className="text-4xl font-display text-amber-900">${data.metrics.outstanding_balances_usd.toLocaleString()}</div>
              <div className="text-xs text-amber-700 mt-1">Across all active enrollments (snapshot).</div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Tile({ icon: Icon, label, value, big = false }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-4 ${big ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 inline-flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </div>
      <div className={`${big ? "text-3xl" : "text-xl"} font-display text-slate-900 mt-1`}>{value}</div>
    </div>
  );
}
function Sub({ label, value }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}
